import { QueryClient, QueryCache } from "@tanstack/react-query";
import {
  handleError,
  handleMutationError,
  isErrorClaimed,
} from "@/shared/lib/errors";

/**
 * Factory that creates the application QueryClient with standard defaults.
 * App.tsx uses this in Phase 2 once providers are consolidated.
 * Status codes that should never be retried:
 *   401 — handled silently by the Axios interceptor (token refresh)
 *   403 — unauthorized; retrying won't help
 *   404 — resource not found; retrying won't help
 *   440 — session expired; handled via modal
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    // QueryCache.onError is the correct place in React Query v4 to access
    // query.meta — defaultOptions.queries.onError only receives (error),
    // not the Query object, so meta cannot be read there.
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Queries that set meta.silent = true handle their own error UI
        // (e.g. the notification badge) and must never trigger the global
        // full-page error overlay.
        if (query.meta?.silent) {
          return;
        }
        handleError(error as Parameters<typeof handleError>[0]);
      },
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        // 30s staleness window. With staleTime=0 every query refetches the
        // moment a screen re-mounts — list → detail → back to list would
        // hit the server again, and any list page that gates render on
        // `isFetching` would flash a skeleton instead of returning the
        // cached rows. 30s keeps everyday navigation instant while still
        // refreshing data on any genuinely new mount. Individual queries
        // can override this (e.g. /onboarding-v2/bootstrap pins 0).
        staleTime: 30_000,
        retry: (failureCount, error: unknown) => {
          const status = (error as { response?: { status?: number } })?.response
            ?.status;
          if (
            status === 401 ||
            status === 403 ||
            status === 404 ||
            status === 440
          ) {
            return false;
          }
          return failureCount < 1;
        },
      },
      mutations: {
        // Fallback reporter, not the primary one. React Query runs this BEFORE
        // rejecting to the caller, so a page that handles the error itself
        // (useMutationErrorHandler, in a catch) would otherwise be the SECOND
        // toast for one failure — which is exactly what merchants saw.
        //
        // Deferring a macrotask lets the page's rejection handler — a
        // microtask, so always earlier — claim the error first. Nothing
        // claims it → this still fires, just a frame later, so mutations with
        // no page-level handling keep their safety net.
        onError: (error) => {
          setTimeout(() => {
            if (isErrorClaimed(error)) return;
            handleMutationError(
              error as Parameters<typeof handleMutationError>[0],
            );
          }, 0);
        },
      },
    },
  });
}
